import { createPortal } from 'react-dom';
import { InlineAlert, type InlineAlertProps } from './InlineAlert';
import './Toast.css';

/**
 * MVP-462. An `InlineAlert` that floats instead of sitting in the layout.
 *
 * Screen-level notices used to render in flow, so every appearance and dismissal pushed the page
 * down and back up again — a search result the operator was reading slid out from under the pointer
 * the moment a write failed. Owner: "พวกแจ้งเตือนให้ปรับเป็น sticky toast ลอย ทั้งหมด ต้องไม่แทรกใน
 * div ไม่สวย".
 *
 * Sticky, not timed: this is the same alert with the same lifetime, moved off the page. It leaves
 * when its own state clears, exactly as before. `Toast`/`useToast` next door is the other thing —
 * an imperative receipt that counts itself down — and a failed write is not a receipt. Keeping the
 * state declarative is also what makes every call site a one-word change rather than a rewrite of
 * how it tracks its error.
 *
 * NOT for an alert that belongs to a control: a field's validation error, a dialog's own failure, a
 * panel's empty-state explanation. Those read as a caption on the thing above them, and floating
 * them to the top of the window severs that. They stay `InlineAlert`.
 */

/** One region for every floating alert, so two at once stack instead of landing on each other.
 * Created on first use rather than mounted by a provider: an alert can be raised from a screen that
 * sits outside `ToastProvider` (the guest page, the invite redeem screen), and a detached module
 * here needs no tree at all. */
let region: HTMLElement | null = null;
function alertRegion(): HTMLElement {
  if (region?.isConnected) return region;
  region = document.createElement('div');
  region.className = 'alert-toast-region';
  document.body.appendChild(region);
  return region;
}

export function AlertToast(props: InlineAlertProps) {
  return createPortal(<InlineAlert {...props} />, alertRegion());
}
